import { useToast } from '@chakra-ui/react'
import {
  CollectionReference,
  collection,
  doc,
  updateDoc,
} from 'firebase/firestore'
import useCurrentReport from '../../../../hooks/useCurrentReport'
import useFirestoreUser from '../../../../hooks/useFirestoreUser'
import useNavigateNext from '../../../../hooks/useNavigateNext'
import useActiveUid from '../../../../hooks/useActiveUid'
import useUpdateReportDoc from '../../../../hooks/useUpdateReportDoc'
import useStore from '../../../../store/useStore'
import FirestoreUser, { StageStatus } from '../../../../types/FirestoreUser'
import { StagesFormState } from '../utils/types'
import { coreProgramsTimeLength } from '../utils/constants'
import { getEffectiveServices } from '../../../../utils/firebaseServiceContext'

export const useStagesFormState = (): StagesFormState => {
  const updateReport = useUpdateReportDoc()
  const navNext = useNavigateNext()
  const report = useCurrentReport()
  const user = useFirestoreUser()
  const toast = useToast()
  const userStages = useStore((state) => state.firestoreUser?.stages || {})
  const uid = useActiveUid()

  const coreProgramsCounts: Record<string, number> = { live: 0, recording: 0 }

  const update = async ({
    id,
    status,
    completions,
  }: {
    id: string
    status: StageStatus
    completions?: ({ month: number; year: number } | null)[]
  }) => {
    if (!uid) return null
    const coll = collection(
      getEffectiveServices().firestore,
      'users'
    ) as CollectionReference<FirestoreUser>

    // Prepare the stage data - either just status (backward compatibility) or full StageData
    const stageData = completions ? { status, completions } : status

    return Promise.all([
      updateDoc(doc(coll, uid), {
        stages: {
          ...userStages,
          [id]: stageData,
        },
      }),
      updateReport({
        stageProgress: {
          ...report?.stageProgress,
          [id]: status,
        },
      }),
    ])
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!report?.sundays) {
      return toast({
        status: 'info',
        title: 'Incomplete',
        description:
          'Please fill out the Live Sessions section. If you did not attend or watch any sessions, check "None"',
      })
    }
    updateReport({ stages: true })
    navNext()
  }

  if (report) {
    Object.entries(report?.sundays ?? {}).forEach(([date, eventType]) => {
      const dayOfWeek = new Date(date).getUTCDay()
      coreProgramsCounts[eventType] += coreProgramsTimeLength[dayOfWeek]
    })
  }

  return {
    handleSubmit,
    update,
    coreProgramsCounts,
    user: user || null,
  }
}
